import React, { useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import EngageSessionPanel from "@/components/engage/EngageSessionPanel";
import ActiveSessionBanner from "@/components/engage/ActiveSessionBanner";
import BootSplash from "@/features/customerWizard/BootSplash";
import HowItWorksButton from "@/components/common/HowItWorksButton";
import { Loader2, MapPin, Tag, LogIn, ArrowRight, Store } from "lucide-react";

/**
 * Iter-288 — White-label QR scan page.
 *
 * Public entrypoint from a partner store's till QR. The URL is generated
 * by the backend as `https://<domain>/engage/{qr_code_id}` and this route
 * MUST look like the store's own app — brand colour, store logo, store
 * welcome message. AllSale is reduced to a small "Powered by AllSale"
 * footer inside the page (App.js hides its global header/footer for this
 * route entirely).
 *
 * Backend contract:
 *   GET  /api/classifieds/engagement/scan/{qr_code_id}
 *   Header:  X-App-Platform: nz_classified
 *   Response: {
 *     vendor_id, business_name, discount_percent, active,
 *     branding: { logo, brand_color, tagline, welcome_message },
 *     catalogue: [ {type:"deal", ...} | {type:"listing", ...} ],
 *     physical_address, social_handles, fee_mode
 *   }
 */

const ENGAGEMENT_HEADERS = { "X-App-Platform": "nz_classified" };
// Iter-288e — the demoallsale ingress strips custom headers, so every
// engagement call also appends ?platform=nz_classified (backend accepts
// either channel; we send both for defence in depth).
const ENGAGEMENT_PARAM = { platform: "nz_classified" };
const FALLBACK_COLOR = "#1F2937"; // neutral slate-800 when no brand_color

function initialsOf(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

// Pick readable text colour (white vs slate-900) based on YIQ luminance.
function contrastText(hex) {
  try {
    const h = (hex || "").replace("#", "");
    if (h.length !== 6) return "#ffffff";
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    // Iter-562 §1 — founder fix: dark ink kicks in earlier so white
    // text never lands on moderately light brand colours.
    return yiq >= 140 ? "#0F172A" : "#ffffff";
  } catch { return "#ffffff"; }
}

export default function EngagePage() {
  const { qrCodeId } = useParams();
  // Iter-319 — Stripe checkout returns to /engage/{qr}?paid=1&sid=ses_…
  // In that case we must NOT create a new session: render the paid card
  // for the returned session instead.
  const [searchParams] = useSearchParams();
  const paidReturn = searchParams.get("paid") === "1";
  const paidSessionId = searchParams.get("sid") || null;
  // Iter-613 — notification deep-link: resume a live session directly.
  const resumeId = searchParams.get("resume") || null;
  // Iter-319 — Stripe cancel_url returns to ?cancelled=1: the session is
  // still open, so the normal panel resumes; just tell the customer.
  const cancelledReturn = searchParams.get("cancelled") === "1";
  const cancelToastShown = React.useRef(false);
  useEffect(() => {
    if (cancelledReturn && !cancelToastShown.current) {
      cancelToastShown.current = true;
      toast.info("Payment cancelled — you can pay again when you're ready.");
    }
  }, [cancelledReturn]);
  const { user } = useAuth();
  const nav = useNavigate();

  const scanQ = useQuery({
    queryKey: ["engage-scan", qrCodeId],
    enabled: Boolean(qrCodeId),
    retry: 1,
    queryFn: async () => {
      const { data } = await sharedApi.get(
        `/classifieds/engagement/scan/${qrCodeId}`,
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
  });

  const brand = scanQ.data?.branding || {};
  const brandColor = brand.brand_color || FALLBACK_COLOR;
  const textOnBrand = useMemo(() => contrastText(brandColor), [brandColor]);
  const catalogue = Array.isArray(scanQ.data?.catalogue) ? scanQ.data.catalogue : [];

  // Iter 694 — no-auth till probe while signed out: a camera scan opens a
  // fresh tab, and the customer must SEE the waiting sale + sign in to
  // connect (never "ask the staff" dead-ends).
  const tillQ = useQuery({
    queryKey: ["engage-till-status", qrCodeId],
    enabled: Boolean(qrCodeId) && !user,
    refetchInterval: 3_000,
    retry: false,
    queryFn: async () => {
      const { data } = await sharedApi.get(
        `/classifieds/engagement/qr/${qrCodeId}/till-status`,
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
  });
  const waitingSale = tillQ.data?.till_sale_waiting ? tillQ.data?.sales?.[0] || null : null;

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="engage-page">
      <Seo
        title={scanQ.data?.business_name || "Store"}
        description={brand.tagline || "Scan, pay & save"}
      />

      {/* Iter 641 — branded store-logo boot splash after a QR tap (never on
          ?resume= deep links or the ?paid=1 Stripe return). The static layer
          in public/index.html covers the JS bundle boot with the same look. */}
      {Boolean(qrCodeId) && !resumeId && !paidReturn && (
        <BootSplash
          active
          vendorName={scanQ.data?.business_name}
          vendorLogo={brand.logo}
          loaded={Boolean(scanQ.data)}
          error={scanQ.isError}
        />
      )}

      {scanQ.isLoading && (
        <div className="flex-1 grid place-items-center py-24" data-testid="engage-loading">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {scanQ.isError && (
        <div className="flex-1 grid place-items-center px-6 py-24 text-center" data-testid="engage-error">
          <div>
            <div className="w-14 h-14 rounded-full bg-slate-100 text-slate-400 grid place-items-center mx-auto">
              <Store className="w-6 h-6" />
            </div>
            <h1 className="mt-4 font-display text-xl font-black text-slate-900">This store code isn&apos;t active</h1>
            <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">
              The QR you scanned couldn&apos;t be found or has been deactivated. Please ask the store to check their code.
            </p>
          </div>
        </div>
      )}

      {scanQ.data && (
        <>
          {/* Store-branded header — takes over the top of the page. */}
          <header
            data-testid="engage-brand-header"
            style={{ backgroundColor: brandColor, color: textOnBrand }}
            className="px-6 pt-8 pb-10"
          >
            <div className="max-w-md mx-auto">
              <div className="flex items-center gap-4">
                {brand.logo ? (
                  <img
                    src={brand.logo}
                    alt=""
                    className="w-14 h-14 rounded-2xl object-cover bg-white/10 ring-1 ring-white/20 shrink-0"
                    data-testid="engage-brand-logo"
                  />
                ) : (
                  <div
                    className="w-14 h-14 rounded-2xl grid place-items-center bg-white/15 ring-1 ring-white/25 shrink-0 font-display text-xl font-black"
                    data-testid="engage-brand-initials"
                    style={{ color: textOnBrand }}
                  >
                    {initialsOf(scanQ.data.business_name)}
                  </div>
                )}
                <div className="min-w-0">
                  <h1 className="font-display text-2xl md:text-3xl font-black leading-tight" data-testid="engage-business-name">
                    {scanQ.data.business_name}
                  </h1>
                  {brand.tagline && (
                    <p className="text-sm opacity-85 mt-0.5" data-testid="engage-tagline">
                      {brand.tagline}
                    </p>
                  )}
                </div>
              </div>

              {brand.welcome_message && (
                <p className="mt-6 text-lg md:text-xl font-semibold leading-snug" data-testid="engage-welcome">
                  {brand.welcome_message}
                </p>
              )}

              {typeof scanQ.data.discount_percent === "number" && scanQ.data.discount_percent > 0 && (
                <div
                  data-testid="engage-discount-badge"
                  className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-white/15 border border-white/25 px-3 py-1 text-xs font-black uppercase tracking-widest"
                >
                  <Tag className="w-3.5 h-3.5" />
                  Save {Number(scanQ.data.discount_percent).toFixed(0)}% today
                </div>
              )}

              {scanQ.data.physical_address && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs opacity-80">
                  <MapPin className="w-3.5 h-3.5" />
                  {scanQ.data.physical_address}
                </div>
              )}
            </div>
          </header>

          <main className="flex-1 px-6 py-8">
            <div className="max-w-md mx-auto space-y-8">
              {/* Iter-643 — "How it works" video at the top of the scan
                  card, before the wizard/login prompt. */}
              <div className="text-center" data-testid="engage-howto-row">
                <HowItWorksButton video="customer" />
              </div>
              {/* Iter-318 — signed-in customers get their live session
                  (short code, bill, review, pay) immediately. */}
              {user && (
                <>
                  {/* Iter 695 — resume banner for a live session at ANOTHER
                      store (this store's own session renders below). */}
                  <ActiveSessionBanner skipQr={qrCodeId} />
                  <EngageSessionPanel
                    qrCodeId={qrCodeId}
                    brandColor={brandColor}
                    textOnBrand={textOnBrand}
                    paidReturn={paidReturn}
                    paidSessionId={paidSessionId}
                    resumeId={resumeId}
                  />
                </>
              )}

              {/* Login/signup prompt stays INSIDE the branded frame.
                  Iter 694 — till-aware: show the waiting sale and get the
                  customer signed in; the claim fires the moment they're
                  back on this exact URL. */}
              {!user && (
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5" data-testid="engage-login-prompt">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-full grid place-items-center shrink-0"
                      style={{ backgroundColor: `${brandColor}22`, color: brandColor }}
                    >
                      <LogIn className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      {waitingSale ? (
                        <>
                          <div className="font-bold text-slate-900 text-sm" data-testid="engage-till-waiting-signin">
                            🧾 Sale{" "}
                            <span className="font-black">
                              {waitingSale.short_code}
                              {waitingSale.bill_amount != null
                                ? ` · $${Number(waitingSale.bill_amount).toFixed(2)}`
                                : ""}
                            </span>{" "}
                            is waiting at the till — <span className="font-black">sign in to connect</span>
                          </div>
                          <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">
                            Sign in and this screen connects to your sale straight away.
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="font-bold text-slate-900 text-sm">Ready to save?</div>
                          <p className="mt-0.5 text-xs text-slate-500 leading-relaxed" data-testid="engage-till-idle-signin">
                            Ask the staff to start your sale at the till — sign in meanwhile so it
                            connects the moment they do.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => nav(`/login?next=${encodeURIComponent(`/engage/${qrCodeId}`)}`)}
                    data-testid="engage-login-cta"
                    style={{ backgroundColor: brandColor, color: textOnBrand }}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold py-3 text-sm shadow-card transition-transform hover:scale-[1.01]"
                  >
                    {waitingSale ? "Sign in to connect" : "Continue"} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* "While you wait" catalogue strip — hides on empty. */}
              {catalogue.length > 0 && (
                <section data-testid="engage-catalogue">
                  <div className="flex items-center justify-between">
                    <h2 className="font-display text-base font-black text-slate-900">
                      While you wait…
                    </h2>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      {catalogue.length} live
                    </span>
                  </div>
                  <div className="mt-3 -mx-6 px-6 flex gap-3 overflow-x-auto snap-x pb-2">
                    {catalogue.map((item, i) => (
                      <CatalogueCard key={`${item.type}-${item.id}-${i}`} item={item} brandColor={brandColor} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </main>

          <footer className="px-6 pb-6 pt-2 text-center" data-testid="engage-footer">
            <div className="text-[11px] text-slate-400">
              Powered by <span className="font-bold text-slate-500">AllSale</span> ·{" "}
              <Link to="/policies/terms" className="hover:underline text-slate-500">
                Terms
              </Link>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

function CatalogueCard({ item, brandColor }) {
  const isDeal = item.type === "deal";
  const to = isDeal ? `/deals/${item.id}` : `/ad/${item.id}`;
  const priceLabel = isDeal
    ? (item.price_label || (item.price != null ? `$${item.price}` : null))
    : (item.price_type === "free" ? "Free" : item.price != null ? `$${item.price}` : null);
  return (
    <Link
      to={to}
      className="snap-start shrink-0 w-40 rounded-2xl overflow-hidden bg-white border border-slate-200 hover:shadow-card transition-shadow"
      data-testid={`engage-catalogue-card-${item.type}-${item.id}`}
    >
      <div className="aspect-square bg-slate-100 overflow-hidden">
        {item.image ? (
          <img src={item.image} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div
            className="w-full h-full grid place-items-center text-3xl"
            style={{ backgroundColor: `${brandColor}12`, color: brandColor }}
          >
            {isDeal ? "🎟" : "🏷"}
          </div>
        )}
      </div>
      <div className="p-2.5">
        <div className="text-xs font-bold text-slate-900 line-clamp-2 min-h-[2.25rem]">
          {item.title}
        </div>
        {priceLabel && (
          <div className="mt-1 flex items-center gap-1.5">
            <span className="text-sm font-black text-slate-900">{priceLabel}</span>
            {isDeal && item.original_value != null && item.original_value > item.price && (
              <span className="text-[10px] text-slate-400 line-through">${item.original_value}</span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
